
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import EditorTopbar from "./EditorTopbar";
import UploadFile from "./viewer/UploadFile";
import PdfViewer from "./viewer/PdfViewer";
import {getJobResult} from "../api/jobRoutes";
import {JOB_STATUS} from "../constants/status";
import DocumentInspector from "./content/DocumentInspector";

export default function EditorPage() {
    const { jobId } = useParams();

    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);

    const [leftWidth, setLeftWidth] = useState(45);
    const [isDragging, setIsDragging] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hoveredChunkId, setHoveredChunkId] = useState(null);

    const fetchJob = useCallback(async () => {
        try {
            setLoading(true);

            const response = await getJobResult(jobId)

            setJob(response);

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [jobId]);

    useEffect(() => {
        if (!jobId) return;
        fetchJob();
    }, [jobId, fetchJob]);

    const handleMouseDown = () => {
        setIsDragging(true);

        const handleMouseMove = (e) => {
            const newWidth = (e.clientX / window.innerWidth) * 100;

            if (newWidth >= 35 && newWidth <= 80) {
                setLeftWidth(newWidth);
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);

            window.removeEventListener(
                "mousemove",
                handleMouseMove
            );

            window.removeEventListener(
                "mouseup",
                handleMouseUp
            );
        };

        window.addEventListener(
            "mousemove",
            handleMouseMove
        );

        window.addEventListener(
            "mouseup",
            handleMouseUp
        );
    };

    if (loading) {
        return (
            <Box
                sx={{
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: "100vh",
                width: "100%",
                display: "flex",
                overflow: "hidden",
                background: "#f4f4f5",
            }}
        >
            {/* Left side */}
            <Box
                sx={{
                    width: `${leftWidth}%`,
                    display: "flex",
                    flexDirection: "column",
                    borderRight: "1px solid #e4e4e7",
                    background: "#f8f8f8",
                    transition: isDragging
                        ? "none"
                        : "width .15s ease",
                }}
            >
                <EditorTopbar job={job} fetchJob={fetchJob} />

                {/* Conditional content */}
                {job?.status === JOB_STATUS.PENDING ? (
                    <UploadFile
                        job={job}
                        onUploadSuccess={() => {
                            fetchJob();
                        }}
                    />
                ) : (
                    <PdfViewer
                        pages={job?.file?.pages}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        hoveredChunkId={hoveredChunkId}
                        setHoveredChunkId={setHoveredChunkId}
                    />
                )}
            </Box>

            {/* Resize bar */}
            <Box
                onMouseDown={handleMouseDown}
                sx={{
                    width: 6,
                    cursor: "col-resize",
                    background: isDragging
                        ? "#7c3aed"
                        : "transparent",
                    transition: "background .2s",
                    position: "relative",
                    zIndex: 20,

                    "&:hover": {
                        background: "#7c3aed",
                    },
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform:
                            "translate(-50%, -50%)",
                        width: 2,
                        height: 80,
                        borderRadius: 99,
                        background:
                            "rgba(124,58,237,.3)",
                    }}
                />
            </Box>
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    background: "white",
                }}
            >
                <DocumentInspector job={job} currentPage={currentPage} hoveredChunkId={hoveredChunkId}
                                   setHoveredChunkId={setHoveredChunkId}
                                   leftWidth={leftWidth}
                />
            </Box>
        </Box>
    );
}